import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  auctionApi,
  Bid,
  BidPaymentStatus,
  BidStatus,
  PublicAuctionWithTribe,
} from 'src/api/auctionApi';
import { Circle, circleApi, MemberRole } from 'src/api/circles';

import { Environment } from 'src/const/constants';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { AuctionUtils } from 'src/pages/Auction/AuctionUtils';
import { selectCurrentUser } from 'src/redux/slices/loggedUserSlice';
import { currentUser } from 'src/redux/slices/userSlice';
import { BidInfo } from '../BidInfo/BidInfo';
import { CircleAvatar } from '../CircleAvatar/CircleAvatar';

import { useAuctionCloseCountDown } from 'src/hooks/Auction/useAuctionCloseCountDown';
import { BidResult } from '../BidInfo/BidResultEnum';
import { JoinModal } from '../JoinModal/JoinModal';
import styles from './AuctionInfo.module.scss';

// TODO Check location for query param ...?stripe_status=PROCESSING, and
// send update to the API to update the status
// @see https://dunbarapp.atlassian.net/browse/DEV-359

export const AuctionInfo: FC<{
  auction: PublicAuctionWithTribe;
  circle: Circle;
  userBidData: Bid | null;
  onRefreshAuctionData?: () => void;
}> = ({ auction, circle, userBidData, onRefreshAuctionData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const getCurrentUser = useAppSelector(currentUser);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isLeader, setIsLeader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    open_seat_count,
    closeDate,
    status: auction_status,
    bidDetails,
  } = auction;
  const { tribe_name, tribe_avatar, leader_name, _id: circleId } = circle;
  const user = useAppSelector(selectCurrentUser);

  const isAuctionClosed = useAuctionCloseCountDown(closeDate);

  // TODO move side effect into parent to keep children presentational
  useEffect(() => {
    const checkUserRole = async () => {
      if (!user || !circleId) {
        setIsLeader(false);
        setIsLoading(false);
        return;
      }

      try {
        const members = await circleApi.getMembersOfCircle(circleId);
        const currentUserMember = members.find(
          (member: any) => member.user._id === user._id
        );

        if (currentUserMember && currentUserMember.role === MemberRole.Member) {
          navigate(`${PathRoutes.UserCircle.replace(':id', circleId)}`);
        }

        const hasLeadershipRole =
          currentUserMember &&
          (currentUserMember.role === MemberRole.Leader ||
            currentUserMember.role === MemberRole.CoLeader);

        setIsLeader(hasLeadershipRole ?? false);
      } catch (error) {
        setIsLeader(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserRole();
  }, [user, circleId, isLeader, navigate]);

  const handleRefreshAuctionData = () => {
    if (onRefreshAuctionData) {
      onRefreshAuctionData();
    }
  };

  const onOpenBid = (paymentRedirectUrl?: string) => {
    if (!getCurrentUser) {
      return handleAuthRedirect();
    }

    if (paymentRedirectUrl) {
      window.location.href = paymentRedirectUrl;
      return;
    }

    setIsOpenModal(true);
  };

  const onCancelBid = async (bidId: string): Promise<void> => {
    await auctionApi.deleteBid(bidId);
    handleRefreshAuctionData();
  };

  const onAuthRedirect = () => {
    if (!getCurrentUser) {
      return handleAuthRedirect();
    }
  };

  const handleAuthRedirect = () => {
    const redirectUrl = encodeURIComponent(location.pathname);
    navigate(`${PathRoutes.LoginRoute}?redirectUrl=${redirectUrl}`);
    return;
  };

  const bidResult = userBidData?.bid_amount_usd
    ? AuctionUtils.getBidResult(
        userBidData?.payment_status,
        userBidData?.bid_status,
        auction_status,
        isAuctionClosed,
        userBidData?.is_winning_bid
      )
    : null;

  const shouldShowBidButton =
    !isAuctionClosed &&
    !isLeader &&
    bidResult !== BidResult.AUCTION_CLOSING &&
    userBidData?.bid_status !== BidStatus.PROCESSING;
  const showCancelBidButton =
    userBidData?.bid_status === BidStatus.PENDING &&
    bidResult !== BidResult.AUCTION_CLOSING;
  let bidButtonLabel;
  if (userBidData?.stripeSession?.redirectUrl) {
    bidButtonLabel =
      userBidData?.payment_status === BidPaymentStatus.PAYMENT_HOLD
        ? `Complete Bid of ${AuctionUtils.formatDollars(userBidData?.stripeSession?.amount)}`
        : (bidButtonLabel = `Complete Payment of ${AuctionUtils.formatDollars(userBidData?.stripeSession?.amount)}`);
  } else {
    bidButtonLabel = userBidData?.bid_amount_usd
      ? 'Increase Bid'
      : 'Enter a Bid';
  }

  return (
    <div className={styles.container}>
      <CircleAvatar
        circleAvatarUrl={tribe_avatar.url}
        circleName={tribe_name}
      />
      <section className={styles.content}>
        <h1>{tribe_name}</h1>
        <h2>{leader_name}</h2>

        <BidInfo
          // TODO reduce passed props this is getting verbose
          openSeatCount={open_seat_count}
          lowestAvailableBid={bidDetails.lowestAvailableBid}
          closeDate={closeDate}
          bidAmountUsd={userBidData?.bid_amount_usd}
          bidResult={bidResult}
        />

        {shouldShowBidButton && !isLoading && (
          <button
            className={clsx(styles.button, styles['button--primary'])}
            onClick={() => onOpenBid(userBidData?.stripeSession?.redirectUrl)}
          >
            {bidButtonLabel}
          </button>
        )}

        {/*
          // TODO: API needs to re-calculate winners
          // Need to iterate through all the bids and promote a bid by setting `is_winning_bid` to true
         */}
        {process.env.REACT_APP_API_ENV !== Environment.PROD &&
          showCancelBidButton &&
          !isLoading && (
            <button
              className={clsx(styles.button, styles['button--primary'])}
              onClick={() => onCancelBid(userBidData._id)}
            >
              Cancel Bid
            </button>
          )}

        {!user && (
          <small>
            To make a bid, you need to
            <button
              type="button"
              className={clsx(
                styles.link,
                styles['link__button'],
                styles['link--primary']
              )}
              onClick={onAuthRedirect}
            >
              Login or Sign up
            </button>
          </small>
        )}
      </section>

      <JoinModal
        // TODO reduce passed props this is getting verbose
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        tribe_name={tribe_name}
        tribe_avatar_url={tribe_avatar.url}
        leader_name={leader_name}
        open_seat_count={open_seat_count}
        price={AuctionUtils.getMinimumBidAmount(
          auction,
          userBidData?.bid_amount_usd
        )}
        closeDate={closeDate}
        existingBid={userBidData}
        bidResult={bidResult}
        onRefreshAuctionData={handleRefreshAuctionData}
      />
    </div>
  );
};
